import { useState, useEffect, useCallback } from 'react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Phone, Clock, Timer, CalendarDays } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { apiRequest } from '@/lib/api';

// ─── Types ───

interface Stats {
  total_calls: number;
  total_minutes: number;
  avg_duration: number;
  calls_today: number;
  calls_per_day: Array<{ date: string; count: number }>;
  call_types: Array<{ type: string; count: number }>;
  sentiments: Array<{ sentiment: string; count: number }>;
  calls_by_agent: Array<{ agent: string; count: number }>;
}

interface AgentOption {
  id: string;
  name: string;
}

// ─── Helpers ───

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getDateFrom(preset: string): string | undefined {
  const now = new Date();
  switch (preset) {
    case '24h': return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    case '7d': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    case '30d': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    case 'all': return undefined;
    default: return undefined;
  }
}

const COLORS = ['#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#10B981'];

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#22C55E',
  neutral: '#94A3B8',
  negative: '#F97316',
  unknown: '#D1D5DB',
};

// ─── Stat Card ───

function StatCard({ label, value, sub, icon: Icon }: { label: string; value: string | number; sub?: string; icon: typeof Phone }) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="rounded-lg bg-primary/10 p-2">
          <Icon className="h-4 w-4 text-primary" />
        </div>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

// ─── Main Page ───

export function InsightsPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('30d');
  const [agentId, setAgentId] = useState<string | undefined>(undefined);
  const [agents, setAgents] = useState<AgentOption[]>([]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const dateFrom = getDateFrom(datePreset);
      if (dateFrom) params.set('date_from', dateFrom);
      if (agentId) params.set('agent_id', agentId);
      const query = params.toString();
      const data = await apiRequest<Stats>(`/calls/stats${query ? `?${query}` : ''}`);
      setStats(data);
    } catch (e) {
      console.error('Failed to load stats:', e);
    }
    setLoading(false);
  }, [datePreset, agentId]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    apiRequest<{ agents: AgentOption[] }>('/studio/agents').then((res) => {
      setAgents(res.agents || []);
    }).catch(() => {});
  }, []);

  if (loading || !stats) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Insights</h1>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  // Format date labels for bar chart
  const callsPerDay = stats.calls_per_day.map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Insights</h1>

        {/* Filters */}
        <div className="flex gap-3">
          <Select
            value={agentId || 'all'}
            onValueChange={(v) => setAgentId(v === 'all' ? undefined : v)}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 rounded-md border p-1">
            {[
              { value: '24h', label: '24h' },
              { value: '7d', label: '7 days' },
              { value: '30d', label: '30 days' },
              { value: 'all', label: 'All' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDatePreset(opt.value)}
                className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  datePreset === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total Calls" value={stats.total_calls} icon={Phone} />
        <StatCard label="Total Minutes" value={stats.total_minutes} icon={Clock} />
        <StatCard label="Avg Duration" value={formatDuration(stats.avg_duration)} icon={Timer} />
        <StatCard label="Calls Today" value={stats.calls_today} icon={CalendarDays} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Calls per day */}
        {callsPerDay.length > 1 && (
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="text-sm font-semibold">Calls per Day</h3>
            <p className="text-xs text-muted-foreground mb-4">Daily call volume over time</p>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={callsPerDay}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Calls by agent */}
        {stats.calls_by_agent.length > 0 && (
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="text-sm font-semibold">Calls by Agent</h3>
            <p className="text-xs text-muted-foreground mb-4">Volume per AI agent</p>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.calls_by_agent} layout="vertical">
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis dataKey="agent" type="category" tick={{ fontSize: 11 }} width={100} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {stats.calls_by_agent.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Call types */}
        {stats.call_types.length > 0 && (
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="text-sm font-semibold">Call Types</h3>
            <p className="text-xs text-muted-foreground mb-4">Breakdown by call category</p>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stats.call_types}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={40}
                  paddingAngle={2}
                >
                  {stats.call_types.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Sentiment */}
        {stats.sentiments.length > 0 && (
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="text-sm font-semibold">Sentiment</h3>
            <p className="text-xs text-muted-foreground mb-4">How callers felt during conversations</p>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stats.sentiments}
                  dataKey="count"
                  nameKey="sentiment"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={40}
                  paddingAngle={2}
                >
                  {stats.sentiments.map((entry, i) => (
                    <Cell key={i} fill={SENTIMENT_COLORS[entry.sentiment] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
